import type { FC, ReactNode } from 'react';
import { cn } from '../lib/utils';
import {
  MessageScroller,
  MessageScrollerButton,
  MessageScrollerContent,
  MessageScrollerProvider,
  MessageScrollerViewport
} from './message-scroller';

interface ChatTranscriptProps {
  /** `MessageScrollerItem`s, one per turn. Anchor user turns with `scrollAnchor`. */
  children: ReactNode;
  /** A reply is on its way; exposed as `aria-busy` on the log. */
  busy?: boolean;
  /** Accessible name for the scrolling log. */
  label?: string;
  /** Applied to the content column, e.g. to cap its width in a wide panel. */
  contentClassName?: string;
  className?: string;
}

/**
 * The scrolling conversation. Opens on the latest user turn, follows a reply
 * while it streams (and the reader is at the bottom), and offers a
 * jump-to-latest button once they scroll away. Following is tied to `busy` so
 * late layout, such as a code block finishing highlighting, does not drag a
 * settled transcript to the bottom.
 */
export const ChatTranscript: FC<ChatTranscriptProps> = ({
  children,
  busy = false,
  label = 'Conversation',
  contentClassName,
  className
}) => (
  <MessageScrollerProvider autoScroll={busy} defaultScrollPosition="last-anchor">
    <MessageScroller className={cn('flex-1', className)}>
      <MessageScrollerViewport aria-label={label}>
        <MessageScrollerContent
          aria-busy={busy}
          className={cn('gap-8 px-6 pt-7 pb-6 max-sm:px-4', contentClassName)}
        >
          {children}
        </MessageScrollerContent>
      </MessageScrollerViewport>
      <MessageScrollerButton
        variant="outline"
        className="size-9 border-border-strong bg-[var(--z-surface-solid)] shadow-lg hover:bg-[var(--z-surface-raised)]"
      />
    </MessageScroller>
  </MessageScrollerProvider>
);
